<template>
    <v-dialog v-model="dialogEditShow" max-width="700px">
      <v-card class="pa-4 pa-sm-8">
        <v-btn class="button-close ma-4" icon @click="dialogEditShow = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <h2 class="text-center">Данные корпоративного клиента</h2>
        <form @submit.prevent="editClientConfirm" class="mt-6">
          <div class="container">
            <div class="row">
              <div class="col-md-6">
                <v-text-field label="Компания *"
                        type="text"
                        v-model="editedItem.company"
                        @input="onInput('company')"
                        @blur="$v.editedItem.company.$touch()"
                        :error-messages="companyErrors"
                        outlined dense />
              </div>
              <div class="col-md-6">
                <CorpPartnerSelect v-model="editedItem.supervisorId" :partners="partners" :type="PARTNER_TYPES.SUPERVISOR" @input="onInput"
                  label="Супервизор" :loading="loadingPartners.load" :disabled="!canChangePartners"
                  :error-messages="partnerErrors" />
              </div>
              <div class="col-md-6">
                <CorpPartnerSelect v-model="editedItem.referrerId" :partners="partners" :type="PARTNER_TYPES.REFERRER" @input="onInput"
                  label="Реферал" :loading="loadingPartners.load" :disabled="!canChangePartners"
                  :error-messages="referrerErrors" />
              </div>

            </div>

            <div class="row">
              <div class="col-md-6">
                <v-text-field label="ИНН"
                        v-model.trim="editedItem.taxpayer_id_number"
                        @input="onInput('taxpayer_id_number')"
                        @blur="$v.editedItem.taxpayer_id_number.$touch()"
                        :error-messages="innErrors"
                        outlined dense />
              </div>
            </div>
            <div v-if="tagsAvailable" class="row">
              <div class="col-12">
                <CorpTagsSelect :value="tagIds" :error-messages="tagErrors" @input="onTagsChange" />
              </div>
            </div>
            <div class="text-subtitle-2 grey--text text--darken-1 mb-2">Менеджер</div>
            <div class="row">
              <div class="col-md-6">
                <v-text-field label="Фамилия *"
                    :value="editedItem.last_name"
                    @input="$v.editedItem.last_name.$model = upperFirst($event), onInput('last_name')"
                    :error-messages="lastnameErrors"
                    outlined dense />
              </div>
              <div class="col-md-6">
                <v-text-field label="Имя *"
                    :value="editedItem.first_name"
                    @input="$v.editedItem.first_name.$model = upperFirst($event), onInput('first_name')"
                    :error-messages="firstnameErrors"
                    outlined dense />
              </div>
            </div>

            <div v-for="(err, ind) in errors.edit" :key="ind" class="error--text">
              <template v-if="!editedItem.hasOwnProperty(err[0]) && !['supervisor', 'referrer'].includes(err[0])">
                <div v-for="(e, i) in err[1]" :key="i">{{e}}</div>
              </template>
            </div>
          </div>

          <div class="row mt-3">
            <div class="col-md-6 mx-auto">
              <v-btn type="submit" class="w-100" color="primary" :loading="loading.edit" :disabled="loading.edit">
                Сохранить
              </v-btn>
            </div>
          </div>
        </form>
      </v-card>
    </v-dialog>
</template>

<script>
/*eslint-disable*/

import { mapActions, mapGetters } from 'vuex';
import { email, maxLength, required } from 'vuelidate/lib/validators';
import { upperFirst } from 'lodash';
import { firstnameValidation, lastnameValidation, innValidation } from '@/common/validationHelpers';
import {
  CORP_COMPANY, EMAIL, FIRST_NAME, LAST_NAME,
} from '@/common/constants/validations';
import { MESSAGES } from '@/common/constants/errorMessages';
import { VueMaskDirective } from 'v-mask';
import { ADMIN } from '@/common/constants/userTypes';
import CorpTagsSelect from '@/components/clients/tags/CorpTagsSelect.vue';
import CorpPartnerSelect from '@/components/clients/CorpPartnerSelect.vue';
import { PARTNER_TYPES, partnerIdOf } from '@/common/models/partnerTypes';

export default {
  validations: {
    editedItem: {
      last_name: { required, lastnameValidation, maxLength: maxLength(LAST_NAME.MAX_LENGTH) },
      first_name: { required, firstnameValidation, maxLength: maxLength(FIRST_NAME.MAX_LENGTH) },
      company: { required, maxLength: maxLength(CORP_COMPANY.MAX_LENGTH) },
      taxpayer_id_number: { innValidation },
    },
  },
  components: { CorpTagsSelect, CorpPartnerSelect },
  directives: {
    mask: VueMaskDirective,
  },
  data: () => ({
    // Теги: набор ID в форме и признак, что админ его менял (без изменений tag_ids не отправляем)
    tagIds: [],
    tagsChanged: false,
    editedItem: {
      id: 0,
      last_name: '',
      first_name: '',
      supervisorId: null,
      referrerId: null,
      company: '',
      taxpayer_id_number: '',
    },
    PARTNER_TYPES,
  }),
  watch: {
    async dialogEditShow(newVal) {
      if (newVal) {
        this.editedItem = (
          ({ id, last_name, first_name, company, taxpayer_id_number }) =>
          ({ id, last_name, first_name, company, taxpayer_id_number: taxpayer_id_number || '' })
          )(this.editedClient);
        // Привязки независимы: у корпа может быть супервизор, реферал, оба или ни одного
        this.$set(this.editedItem, 'supervisorId', partnerIdOf(this.editedClient.supervisor));
        this.$set(this.editedItem, 'referrerId', partnerIdOf(this.editedClient.referrer));
        this.tagIds = Array.isArray(this.editedClient.tags) ? this.editedClient.tags.map((tag) => tag.id) : [];
        this.tagsChanged = false;
        if (this.isAdmin) this.ensureTags();

        // console.log(this.partners?.length, !this.loadingPartners.load)
        if (!this.partners?.length && !this.loadingPartners.load) {
          await this.loadPartners();
        }
      }
    },
  },
  computed: {
    ...mapGetters({
      loading: 'users/clients/loading',
      errors: 'users/clients/errors',
      editedClient: 'users/clients/editedClient',
      partners: 'users/partners/partners',
      loadingPartners: 'users/partners/loading',
      user: 'auth/user',
      tagsAvailable: 'users/corpClientTags/available',
      allTags: 'users/corpClientTags/tags',
    }),
    isAdmin() {
      return !!this.user && this.user.user_type === ADMIN;
    },
    canChangePartners() {
      return this.$store.getters['auth/isAllowed']('change_partner_of_corpclient');
    },
    partnerErrors() {
      const found = this.errors.edit.find((err) => err[0] === 'supervisor');
      return found ? [].concat(found[1]) : [];
    },
    referrerErrors() {
      const found = this.errors.edit.find((err) => err[0] === 'referrer');
      return found ? [].concat(found[1]) : [];
    },
    // Ошибки по тегам: сервер называет поле tag_ids или tags
    tagErrors() {
      const found = this.errors.edit.find((err) => err[0] === 'tag_ids' || err[0] === 'tags');
      return found ? [].concat(found[1]) : [];
    },
    dialogEditShow: {
      get() {
        return this.$store.getters['users/clients/dialogEditShow'];
      },
      set(val) {
        if (!val) {
          this.$v.$reset();
        }
        this.setDialogEditShow(val);
      },
    },
    lastnameErrors() {
      const errors = [];
      if (!this.$v.editedItem.last_name.$dirty) return errors;
      if (!this.$v.editedItem.last_name.required) errors.push(MESSAGES.REQUIRED);
      if (!this.$v.editedItem.last_name.lastnameValidation) errors.push(MESSAGES.INCORRECT_LASTNAME);
      if (!this.$v.editedItem.last_name.maxLength) errors.push(MESSAGES.MAX_LENGTH(LAST_NAME.MAX_LENGTH));

      return this.errors.edit.find((err) => err[0] === 'last_name')?.[1] ?? errors;
    },
    firstnameErrors() {
      const errors = [];
      if (!this.$v.editedItem.first_name.$dirty) return errors;
      if (!this.$v.editedItem.first_name.required) errors.push(MESSAGES.REQUIRED);
      if (!this.$v.editedItem.first_name.firstnameValidation) errors.push(MESSAGES.INCORRECT_FIRSTNAME);
      if (!this.$v.editedItem.first_name.maxLength) errors.push(MESSAGES.MAX_LENGTH(FIRST_NAME.MAX_LENGTH));

      return this.errors.edit.find((err) => err[0] === 'first_name')?.[1] ?? errors;
    },
    companyErrors() {
      const errors = [];
      if (!this.$v.editedItem.company.$dirty) return errors;
      if (!this.$v.editedItem.company.required) errors.push(MESSAGES.REQUIRED);
      if (!this.$v.editedItem.company.maxLength) errors.push(MESSAGES.MAX_LENGTH(CORP_COMPANY.MAX_LENGTH));

      return this.errors.edit.find((err) => err[0] === 'company')?.[1] ?? errors;
    },
    innErrors() {
      const errors = [];
      if (!this.$v.editedItem.taxpayer_id_number.$dirty) return errors;
      if (!this.$v.editedItem.taxpayer_id_number.innValidation) errors.push(MESSAGES.INCORRECT_INN);

      return this.errors.edit.find((err) => err[0] === 'taxpayer_id_number')?.[1] ?? errors;
    },

  },
  methods: {
    ...mapActions({
      clearError: 'users/clients/clearError',
      setDialogEditShow: 'users/clients/setDialogEditShow',
      saveClient: 'users/clients/saveClient',
      loadPartners: 'users/partners/loadPartners',
      ensureTags: 'users/corpClientTags/ensureLoaded',
      reloadTags: 'users/corpClientTags/load',
    }),
    onTagsChange(ids) {
      this.tagIds = ids;
      this.tagsChanged = true;
      if (this.errors.edit.length) this.clearError();
    },
    onInput() {
      if (this.errors.edit.length) this.clearError();
    },
    editClientConfirm() {
      if (this.loading.edit) return;
      this.$v.$touch();
      if (this.editedItem && !this.$v.$invalid) {
        const { supervisorId, referrerId, ...rest } = this.editedItem;
        const form = {
          ...rest,
          taxpayer_id_number: this.editedItem.taxpayer_id_number || null,
        };
        // Нет поля — связь не трогаем, null — снимаем. Отправляем только изменённые привязки и только с правом
        if (this.canChangePartners) {
          const was = (link) => partnerIdOf(link) || null;
          if ((supervisorId || null) !== was(this.editedClient.supervisor)) form.supervisor = supervisorId || null;
          if ((referrerId || null) !== was(this.editedClient.referrer)) form.referrer = referrerId || null;
        }
        // Набор тегов уходит целиком и только если его меняли: [] без изменений сбросил бы чужие теги
        if (this.tagsAvailable && this.tagsChanged) form.tag_ids = this.tagIds;
        this.saveClient(form).then(() => {
          this.dialogEditShow = false;
        }).catch(async () => {
          // Тег могли удалить параллельно: обновляем справочник и убираем из выбора то, чего уже нет.
          // Только если сервер ругается именно на теги, а не на другое поле
          if (!form.tag_ids || !this.tagErrors.length) return;
          await this.reloadTags();
          const known = this.allTags.map((tag) => tag.id);
          this.tagIds = this.tagIds.filter((id) => known.includes(id));
        });
      }
    },
    upperFirst,
  },
};

</script>
